import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import CreateDealButton from '../components/CreateDealButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Search, 
  Filter,
  Flame,
  Thermometer,
  Snowflake,
  Globe,
  Linkedin,
  Building2,
  Mail,
  Eye,
  Sparkles,
  ChevronRight,
  MoreHorizontal,
  Newspaper,
  Award,
  TrendingUp,
  Swords,
  Loader2,
  Send,
  MessageSquareReply,
  CheckCircle2,
  Clock,
  Trash2,
  Pencil,
  Download,
  Share2,
  Users,
  RefreshCcw,
  Upload,
  FileSpreadsheet,
  AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pipeline = () => {
  const [leads, setLeads] = useState([]);
  const [totalLeadCount, setTotalLeadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 100;
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [noneNameCount, setNoneNameCount] = useState(0);
  const [fixingNames, setFixingNames] = useState(false);
  const [filterWarm, setFilterWarm] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPipeline, setFilterPipeline] = useState('all');
  const [pipelines, setPipelines] = useState([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreatePipelineDialog, setShowCreatePipelineDialog] = useState(false);
  const [creatingPipeline, setCreatingPipeline] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    name: '',
    campaign_message: '',
    email_focus: '',
    landing_page_url: '',
    sector: 'none',
    sub_segment: 'none',
    auto_send: true,
  });
  const [sectorMeta, setSectorMeta] = useState([]);
  const [newPipeline, setNewPipeline] = useState({
    name: '',
    campaign_message: '',
    email_focus: '',
    landing_page_url: ''
  });
  const [editingLead, setEditingLead] = useState(null);
  const [generatingOutreach, setGeneratingOutreach] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [selectedPipelineIds, setSelectedPipelineIds] = useState(new Set());
  const [bulkDeletingPipelines, setBulkDeletingPipelines] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [newLead, setNewLead] = useState({
    company_name: '',
    company_domain: '',
    contact_name: '',
    contact_email: '',
    contact_title: '',
    contact_linkedin: '',
    industry: '',
    staff_count: '',
    location: ''
  });
  
  // Sharing state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [sharingPipeline, setSharingPipeline] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const [includeShared, setIncludeShared] = useState(true);
  
  // Generate emails state
  const [generatingPipelineEmails, setGeneratingPipelineEmails] = useState(false);
  const [generateEmailsProgress, setGenerateEmailsProgress] = useState(null);
  const [generatingPipelineFollowups, setGeneratingPipelineFollowups] = useState(false);
  
  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importPipelineId, setImportPipelineId] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPipelines();
    // Fetch sector metadata for dropdowns
    axios.get(`${API}/sectors`).then(r => setSectorMeta(r.data.sectors || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchLeads(1);
  }, [filterWarm, filterStatus, filterPipeline]);

  const fetchPipelines = async () => {
    setLoadingPipelines(true);
    try {
      const response = await axios.get(`${API}/pipelines?include_shared=${includeShared}`);
      setPipelines(response.data);
    } catch (error) {
      console.error('Failed to fetch pipelines:', error);
    } finally {
      setLoadingPipelines(false);
    }
  };

  const handleSharePipeline = async () => {
    if (!shareEmail.trim() || !sharingPipeline) {
      toast.error('Please enter an email address');
      return;
    }
    setSharing(true);
    try {
      await axios.post(`${API}/pipelines/${sharingPipeline.id}/share`, {
        share_with_email: shareEmail
      });
      toast.success(`Pipeline shared with ${shareEmail}`);
      setShareEmail('');
      setShowShareDialog(false);
      setSharingPipeline(null);
      fetchPipelines();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share pipeline');
    } finally {
      setSharing(false);
    }
  };

  // Generate emails for all leads in a pipeline
  const generatePipelineEmails = async (pipelineId) => {
    const pipeline = pipelines.find(p => p.id === pipelineId);
    if (!pipeline) {
      toast.error('Pipeline not found');
      return;
    }

    // Get leads with emails in this pipeline
    const pipelineLeads = leads.filter(l => l.pipeline_id === pipelineId && l.contact_email);
    
    if (pipelineLeads.length === 0) {
      toast.error('No leads with email addresses in this pipeline');
      return;
    }

    const confirmed = window.confirm(
      `Generate AI emails for ${pipelineLeads.length} leads with email addresses in "${pipeline.name}"?\n\nEmails will be added to the Approval Queue for review.`
    );
    
    if (!confirmed) return;

    setGeneratingPipelineEmails(true);
    setGenerateEmailsProgress({ current: 0, total: pipelineLeads.length });
    
    try {
      const response = await axios.post(`${API}/pipelines/${pipelineId}/generate-emails`);
      const result = response.data;
      
      toast.success(
        `Generated ${result.generated} emails! ${result.skipped > 0 ? `(${result.skipped} skipped - already in queue)` : ''}`,
        { duration: 5000 }
      );
      
      // Show errors if any
      if (result.errors?.length > 0) {
        toast.error(`Failed for: ${result.errors.slice(0, 3).join(', ')}${result.errors.length > 3 ? '...' : ''}`, { duration: 5000 });
      }
      
      // Refresh leads to show updated status
      fetchLeads();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate emails');
    } finally {
      setGeneratingPipelineEmails(false);
      setGenerateEmailsProgress(null);
    }
  };

  // Generate follow-up emails for all unreplied leads in a pipeline
  const generatePipelineFollowups = async (pipelineId) => {
    const pipeline = pipelines.find(p => p.id === pipelineId);
    if (!pipeline) {
      toast.error('Pipeline not found');
      return;
    }

    const confirmed = window.confirm(
      `Generate follow-up emails for all unreplied leads in "${pipeline.name}"?\n\nFollow-ups will be added to the Approval Queue for review.`
    );
    
    if (!confirmed) return;

    setGeneratingPipelineFollowups(true);
    
    try {
      const response = await axios.post(`${API}/pipelines/${pipelineId}/generate-followups`);
      const result = response.data;
      
      if (result.generated > 0) {
        toast.success(
          `Generated ${result.generated} follow-ups! ${result.skipped > 0 ? `(${result.skipped} skipped - already in queue)` : ''}`,
          { duration: 5000 }
        );
      } else if (result.skipped > 0) {
        toast.info(`All ${result.skipped} leads already have pending follow-ups`, { duration: 4000 });
      } else {
        toast.info('No unreplied sent emails found for this pipeline', { duration: 4000 });
      }
      
      // Show errors if any
      if (result.errors?.length > 0) {
        toast.error(`Failed for: ${result.errors.slice(0, 3).join(', ')}${result.errors.length > 3 ? '...' : ''}`, { duration: 5000 });
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate follow-ups');
    } finally {
      setGeneratingPipelineFollowups(false);
    }
  };

  const fetchLeads = async (page = currentPage) => {
    try {
      const params = new URLSearchParams();
      if (filterWarm !== 'all') {
        params.append('warm_label', filterWarm);
      }
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (filterPipeline !== 'all') {
        params.append('pipeline_id', filterPipeline);
      }
      params.append('page', page);
      params.append('page_size', PAGE_SIZE);
      
      const response = await axios.get(`${API}/leads?${params.toString()}`);
      const data = response.data;
      
      // Handle new paginated format
      const leadsArray = data.leads || data;
      setLeads(Array.isArray(leadsArray) ? leadsArray : []);
      setTotalLeadCount(data.total_count || leadsArray.length || 0);
      setCurrentPage(data.page || page);
      setTotalPages(data.total_pages || 1);
      
      // Check for "None None" contact names
      const badNames = (Array.isArray(leadsArray) ? leadsArray : []).filter(l => 
        l.contact_name && ['none none', 'none'].includes(l.contact_name.toLowerCase())
      ).length;
      setNoneNameCount(badNames);
    } catch (error) {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  // Create new pipeline
  const handleCreatePipeline = async (e) => {
    e.preventDefault();
    if (!newPipeline.name.trim()) {
      toast.error('Pipeline name is required');
      return;
    }
    
    setCreatingPipeline(true);
    try {
      const response = await axios.post(`${API}/pipelines`, {
        name: newPipeline.name.trim(),
        campaign_message: newPipeline.campaign_message.trim() || null,
        email_focus: newPipeline.email_focus.trim() || null,
        landing_page_url: newPipeline.landing_page_url.trim() || null
      });
      
      toast.success(`Pipeline "${response.data.name}" created`);
      setShowCreatePipelineDialog(false);
      setNewPipeline({ name: '', campaign_message: '', email_focus: '', landing_page_url: '' });
      fetchPipelines();
      
      // Auto-select the new pipeline in filter
      setFilterPipeline(response.data.id);
    } catch (error) {
      console.error('Failed to create pipeline:', error);
      toast.error(error.response?.data?.detail || 'Failed to create pipeline');
    } finally {
      setCreatingPipeline(false);
    }
  };

  const handleUploadSpreadsheet = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }
    if (!uploadForm.name.trim()) {
      toast.error('Pipeline name is required');
      return;
    }

    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadForm.file);
      fd.append('name', uploadForm.name.trim());
      fd.append('campaign_message', uploadForm.campaign_message.trim());
      fd.append('email_focus', uploadForm.email_focus.trim());
      fd.append('landing_page_url', uploadForm.landing_page_url.trim());
      if (uploadForm.sector !== 'none') fd.append('sector', uploadForm.sector);
      if (uploadForm.sub_segment !== 'none') fd.append('sub_segment', uploadForm.sub_segment);
      fd.append('auto_send', uploadForm.auto_send ? 'true' : 'false');

      const res = await axios.post(`${API}/pipelines/import-from-file`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { leads_imported, skipped_empty, skipped_dedup, workflow_id, auto_send_skipped_reason, detected_columns } = res.data;
      const detectedSummary = Object.entries(detected_columns)
        .filter(([, v]) => v)
        .map(([k, v]) => `${k}→${v}`)
        .join(', ');

      let msg = `Imported ${leads_imported} lead${leads_imported === 1 ? '' : 's'}`;
      const skipParts = [];
      if (skipped_dedup > 0) skipParts.push(`${skipped_dedup} duplicate domain${skipped_dedup === 1 ? '' : 's'}`);
      if (skipped_empty > 0) skipParts.push(`${skipped_empty} blank row${skipped_empty === 1 ? '' : 's'}`);
      if (skipParts.length) msg += ` (${skipParts.join(', ')} skipped)`;
      msg += `. Detected: ${detectedSummary}.`;
      if (workflow_id) {
        msg += ` Workflow started - enrichment + send will run in the background.`;
      } else if (auto_send_skipped_reason) {
        msg += ` Auto-send skipped: ${auto_send_skipped_reason}`;
      }
      toast.success(msg, { duration: 10000 });

      setShowUploadDialog(false);
      setUploadForm({ file: null, name: '', campaign_message: '', email_focus: '', landing_page_url: '', sector: 'none', sub_segment: 'none', auto_send: true });
      fetchPipelines();
      setFilterPipeline(res.data.pipeline_id);
    } catch (error) {
      console.error('Failed to upload spreadsheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload spreadsheet');
    } finally {
      setUploadingFile(false);
    }
  };

  // Export pipeline leads to CSV
  const exportPipelineCSV = async () => {
    if (filterPipeline === 'all') {
      toast.error('Please select a specific pipeline to export');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/export/pipeline/${filterPipeline}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const pipelineName = pipelines.find(p => p.id === filterPipeline)?.name || 'pipeline';
      link.setAttribute('download', `${pipelineName.replace(/\s+/g, '_')}_leads.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Pipeline exported to CSV');
    } catch (error) {
      toast.error('Failed to export pipeline');
    }
  };

  // Bulk delete pipelines
  const togglePipelineSelect = (id) => {
    const newSelected = new Set(selectedPipelineIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedPipelineIds(newSelected);
  };

  const toggleSelectAllPipelines = () => {
    if (selectedPipelineIds.size === pipelines.length) {
      setSelectedPipelineIds(new Set());
    } else {
      setSelectedPipelineIds(new Set(pipelines.map(p => p.id)));
    }
  };

  const bulkDeletePipelines = async () => {
    if (selectedPipelineIds.size === 0) return;
    
    if (!window.confirm(`Delete ${selectedPipelineIds.size} pipelines and all their leads?`)) return;
    
    setBulkDeletingPipelines(true);
    try {
      await axios.post(`${API}/pipelines/bulk-delete`, {
        ids: Array.from(selectedPipelineIds)
      });
      toast.success(`Deleted ${selectedPipelineIds.size} pipelines`);
      setSelectedPipelineIds(new Set());
      setFilterPipeline('all');
      fetchPipelines();
      fetchLeads();
    } catch (error) {
      toast.error('Failed to delete pipelines');
    } finally {
      setBulkDeletingPipelines(false);
    }
  };

  // Lead selection functions
  const toggleLeadSelect = (id) => {
    const newSelected = new Set(selectedLeadIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedLeadIds(newSelected);
  };

  const toggleSelectAllLeads = () => {
    if (selectedLeadIds.size === filteredLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(filteredLeads.map(l => l.id)));
    }
  };

  const bulkDeleteLeads = async () => {
    if (selectedLeadIds.size === 0) return;
    
    if (!window.confirm(`Delete ${selectedLeadIds.size} leads permanently?`)) return;
    
    setBulkActionLoading(true);
    try {
      await axios.post(`${API}/leads/bulk-delete`, {
        ids: Array.from(selectedLeadIds)
      });
      toast.success(`Deleted ${selectedLeadIds.size} leads`);
      setSelectedLeadIds(new Set());
      fetchLeads();
      fetchPipelines(); // Update lead counts
    } catch (error) {
      toast.error('Failed to delete leads');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkRemoveFromPipeline = async () => {
    if (selectedLeadIds.size === 0) return;
    
    if (!window.confirm(`Remove ${selectedLeadIds.size} leads from their pipeline? They won't be deleted.`)) return;
    
    setBulkActionLoading(true);
    try {
      await axios.post(`${API}/leads/bulk-remove-from-pipeline`, {
        ids: Array.from(selectedLeadIds)
      });
      toast.success(`Removed ${selectedLeadIds.size} leads from pipeline`);
      setSelectedLeadIds(new Set());
      fetchLeads();
      fetchPipelines(); // Update lead counts
    } catch (error) {
      toast.error('Failed to remove leads from pipeline');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk generate emails for selected leads
  const bulkGenerateEmailsForSelected = async () => {
    if (selectedLeadIds.size === 0) return;

    // Filter selected leads that have email addresses
    const selectedLeads = leads.filter(l => selectedLeadIds.has(l.id));
    const leadsWithEmail = selectedLeads.filter(l => l.contact_email);
    
    if (leadsWithEmail.length === 0) {
      toast.error('No selected leads have email addresses');
      return;
    }

    const confirmed = window.confirm(
      `Generate AI emails for ${leadsWithEmail.length} selected leads with email addresses?\n\nEmails will be added to the Approval Queue for review.`
    );
    
    if (!confirmed) return;

    setBulkActionLoading(true);
    let generated = 0;
    let errors = [];

    try {
      // Generate emails one by one to avoid timeout
      for (const lead of leadsWithEmail) {
        try {
          await axios.post(`${API}/leads/${lead.id}/generate-outreach`);
          generated++;
        } catch (error) {
          errors.push(lead.company_name);
        }
      }

      if (generated > 0) {
        toast.success(`Generated emails for ${generated} leads!`, { duration: 4000 });
      }
      if (errors.length > 0) {
        toast.error(`Failed for: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '...' : ''}`, { duration: 4000 });
      }

      setSelectedLeadIds(new Set());
      fetchLeads();
    } catch (error) {
      toast.error('Failed to generate emails');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const generateOutreach = async (lead) => {
    // Check if lead has contact info
    if (!lead.contact_email && !lead.contact_linkedin) {
      toast.error('Lead must have an email or LinkedIn profile to generate outreach');
      return;
    }
    
    setGeneratingOutreach(lead.id);
    try {
      const response = await axios.post(`${API}/leads/${lead.id}/generate-outreach`);
      toast.success(response.data.message || 'Outreach generated!');
      fetchLeads(); // Refresh to show updated status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate outreach');
    } finally {
      setGeneratingOutreach(null);
    }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      const leadData = {
        ...newLead,
        staff_count: newLead.staff_count ? parseInt(newLead.staff_count) : null,
        signals: []
      };
      await axios.post(`${API}/leads`, leadData);
      toast.success('Lead added successfully');
      setShowAddDialog(false);
      setNewLead({
        company_name: '',
        company_domain: '',
        contact_name: '',
        contact_email: '',
        contact_title: '',
        contact_linkedin: '',
        industry: '',
        staff_count: '',
        location: ''
      });
      fetchLeads();
    } catch (error) {
      toast.error('Failed to add lead');
    }
  };

  const getWarmIcon = (label) => {
    switch (label) {
      case 'hot': return <Flame className="w-4 h-4" />;
      case 'warm': return <Thermometer className="w-4 h-4" />;
      case 'lukewarm': return <Thermometer className="w-4 h-4" />;
      default: return <Snowflake className="w-4 h-4" />;
    }
  };

  const getWarmStyle = (label) => {
    switch (label) {
      case 'hot': return 'score-hot';
      case 'warm': return 'score-warm';
      case 'lukewarm': return 'score-lukewarm';
      default: return 'score-cold';
    }
  };

  const getSignalIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'news': return <Newspaper className="w-3 h-3" />;
      case 'award': return <Award className="w-3 h-3" />;
      case 'linkedin': return <Linkedin className="w-3 h-3" />;
      case 'funding': return <TrendingUp className="w-3 h-3" />;
      case 'competitor': return <Swords className="w-3 h-3" />;
      case 'visitor': return <Eye className="w-3 h-3" />;
      default: return <Sparkles className="w-3 h-3" />;
    }
  };

  const getSignalStyle = (type) => {
    switch (type?.toLowerCase()) {
      case 'news': return 'signal-news';
      case 'award': return 'signal-award';
      case 'linkedin': return 'signal-linkedin';
      case 'funding': return 'signal-funding';
      case 'competitor': return 'signal-competitor';
      case 'visitor': return 'signal-visitor';
      default: return 'signal-news';
    }
  };


  const fixNoneNames = async () => {
    setFixingNames(true);
    try {
      const response = await axios.post(`${API}/leads/fix-none-names`);
      toast.success(response.data.message, { duration: 4000 });
      setNoneNameCount(0);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to fix contact names');
    } finally {
      setFixingNames(false);
    }
  };


  // Import leads from CSV/Excel file
  const handleImportFile = async () => {
    if (!importFile || !importPipelineId) {
      toast.error('Please select a file and pipeline');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const response = await axios.post(
        `${API}/pipelines/${importPipelineId}/import`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      const result = response.data;
      toast.success(result.message, { duration: 5000 });
      
      if (result.errors?.length > 0) {
        toast.error(`Errors: ${result.errors.slice(0, 2).join('; ')}`, { duration: 5000 });
      }

      // Reset and close
      setShowImportDialog(false);
      setImportFile(null);
      setImportPipelineId('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Refresh data
      fetchLeads();
      fetchPipelines();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import file');
    } finally {
      setImporting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'follow_up_needed':
        return (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs font-medium animate-pulse">
            <RefreshCcw className="w-3 h-3 mr-1" />
            Needs Follow-up
          </Badge>
        );
      case 'replied':
        return (
          <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs font-medium animate-pulse">
            <MessageSquareReply className="w-3 h-3 mr-1" />
            Replied
          </Badge>
        );
      case 'sent':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs font-medium">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Sent
          </Badge>
        );
      case 'queued':
        return (
          <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 text-xs font-medium">
            <Clock className="w-3 h-3 mr-1" />
            Queued
          </Badge>
        );
      case 'new':
        return (
          <Badge variant="outline" className="text-slate-400 text-xs font-mono">
            New
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="capitalize text-xs font-mono text-slate-400">
            {status?.replace('_', ' ') || 'Unknown'}
          </Badge>
        );
    }
  };

  const deleteLead = async (leadId, companyName) => {
    if (!window.confirm(`Remove "${companyName}" from pipeline?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success(`Removed ${companyName} from pipeline`);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to remove lead');
    }
  };

  const openEditDialog = (lead) => {
    setEditingLead({
      ...lead,
      contact_linkedin: lead.contact_linkedin || ''
    });
    setShowEditDialog(true);
  };

  const saveLeadEdit = async () => {
    if (!editingLead) return;
    
    setSavingEdit(true);
    try {
      await axios.patch(`${API}/leads/${editingLead.id}`, {
        contact_name: editingLead.contact_name,
        contact_email: editingLead.contact_email,
        contact_title: editingLead.contact_title,
        contact_linkedin: editingLead.contact_linkedin
      });
      toast.success('Lead updated successfully');
      setShowEditDialog(false);
      setEditingLead(null);
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update lead');
    } finally {
      setSavingEdit(false);
    }
  };

  // Filter and sort leads - follow_up_needed at top
  const filteredLeads = leads
    .filter(lead => {
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = (
          lead.company_name?.toLowerCase().includes(search) ||
          lead.contact_name?.toLowerCase().includes(search) ||
          lead.contact_email?.toLowerCase().includes(search)
        );
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (filterStatus !== 'all' && lead.status !== filterStatus) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      // Sort follow_up_needed to top
      const aFollowUp = a.status === 'follow_up_needed' ? 1 : 0;
      const bFollowUp = b.status === 'follow_up_needed' ? 1 : 0;
      if (aFollowUp !== bFollowUp) return bFollowUp - aFollowUp;
      
      // Then sort by warm score (hot > warm > cold)
      const scoreOrder = { hot: 3, warm: 2, cold: 1 };
      const aScore = scoreOrder[a.warm_label] || 0;
      const bScore = scoreOrder[b.warm_label] || 0;
      return bScore - aScore;
    });

  const hotCount = leads.filter(l => l.warm_label === 'hot').length;
  const warmCount = leads.filter(l => l.warm_label === 'warm').length;
  const repliedCount = leads.filter(l => l.status === 'replied').length;
  const followUpCount = leads.filter(l => l.status === 'follow_up_needed').length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-serif text-4xl text-white mb-2">Pipeline Intelligence</h1>
          <p className="text-slate-400">
            {totalLeadCount} leads • {hotCount} hot • {warmCount} warm
            {followUpCount > 0 && (
              <span className="text-amber-400"> • {followUpCount} needs follow-up</span>
            )}
            {repliedCount > 0 && (
              <span className="text-emerald-400"> • {repliedCount} replied</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">

          {/* None Name Fix Banner */}
          {noneNameCount > 0 && (
            <Button
              size="sm"
              onClick={fixNoneNames}
              disabled={fixingNames}
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs"
              data-testid="fix-none-names-btn"
            >
              {fixingNames ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
              )}
              Fix {noneNameCount} bad names
            </Button>
          )}

          {/* Upload Spreadsheet Button */}
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10" data-testid="upload-spreadsheet-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Upload Spreadsheet
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="upload-dialog">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-white">Upload Company List</DialogTitle>
                <DialogDescription className="text-slate-400">
                  CSV or XLSX. We'll auto-detect <span className="text-slate-300">Company</span>, <span className="text-slate-300">Website</span>, <span className="text-slate-300">LinkedIn</span>, <span className="text-slate-300">Description</span> columns. Missing emails are found via Hunter.io during the workflow.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUploadSpreadsheet} className="space-y-4 mt-2">
                <div className="space-y-2">
                  <Label className="text-slate-300">File *</Label>
                  <Input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setUploadForm({...uploadForm, file: e.target.files?.[0] || null})}
                    className="bg-slate-950 border-slate-800 file:bg-slate-800 file:text-white file:border-0 file:mr-3"
                    data-testid="upload-file-input"
                    required
                  />
                  {uploadForm.file && (
                    <p className="text-xs text-slate-500">{uploadForm.file.name} · {(uploadForm.file.size / 1024).toFixed(1)} KB</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Pipeline Name *</Label>
                  <Input
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm({...uploadForm, name: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="e.g., UK Health Tech Q2"
                    data-testid="upload-pipeline-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email Focus</Label>
                  <Input
                    value={uploadForm.email_focus}
                    onChange={(e) => setUploadForm({...uploadForm, email_focus: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="e.g., AI consulting for health tech companies"
                    data-testid="upload-email-focus"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Landing Page URL</Label>
                  <Input
                    value={uploadForm.landing_page_url}
                    onChange={(e) => setUploadForm({...uploadForm, landing_page_url: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="https://your-landing-page.com"
                    data-testid="upload-landing-url"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Sector</Label>
                    <Select value={uploadForm.sector} onValueChange={v => setUploadForm({...uploadForm, sector: v, sub_segment: 'none'})}>
                      <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="upload-sector"><SelectValue placeholder="-" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none">- none -</SelectItem>
                        {sectorMeta.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Sub-segment</Label>
                    <Select
                      value={uploadForm.sub_segment}
                      onValueChange={v => setUploadForm({...uploadForm, sub_segment: v})}
                      disabled={uploadForm.sector === 'none'}
                    >
                      <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="upload-sub-segment"><SelectValue placeholder="-" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none">- none -</SelectItem>
                        {sectorMeta.find(s => s.value === uploadForm.sector)?.sub_segments?.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 -mt-1">Tagging by sector drives AI prompt context, so outreach reads correctly for the audience.</p>
                <div className="space-y-2">
                  <Label className="text-slate-300">Campaign Message / AI Prompt</Label>
                  <textarea
                    value={uploadForm.campaign_message}
                    onChange={(e) => setUploadForm({...uploadForm, campaign_message: e.target.value})}
                    className="w-full min-h-[80px] bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50"
                    placeholder="Custom instructions for AI-generated outreach emails..."
                    data-testid="upload-campaign-message"
                  />
                </div>
                <label className="flex items-start gap-2 p-3 border border-slate-800 rounded-lg bg-slate-950/50 cursor-pointer hover:border-slate-700 transition-colors">
                  <Checkbox
                    checked={uploadForm.auto_send}
                    onCheckedChange={(checked) => setUploadForm({...uploadForm, auto_send: !!checked})}
                    data-testid="upload-auto-send"
                  />
                  <div className="flex-1">
                    <span className="text-sm text-white font-medium">Auto-send via Workflow</span>
                    <p className="text-xs text-slate-500 mt-1">
                      Start an automated workflow that enriches missing emails (Hunter.io) and sends outreach in batches of 20 between 6am-8pm UK time, Mon-Fri.
                    </p>
                  </div>
                </label>
                <div className="flex items-start gap-2 text-xs text-amber-400/80 bg-amber-500/5 border border-amber-500/20 rounded p-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>Large lists may take days to fully process. Rate-limited to 1,400/day. You can pause workflows anytime from the Workflows page.</span>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="ghost" onClick={() => setShowUploadDialog(false)} disabled={uploadingFile}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-violet-600 hover:bg-violet-500"
                    disabled={uploadingFile}
                    data-testid="upload-submit"
                  >
                    {uploadingFile ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Upload &amp; {uploadForm.auto_send ? 'Start' : 'Import'}</>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Create Pipeline Button */}
          <Dialog open={showCreatePipelineDialog} onOpenChange={setShowCreatePipelineDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10" data-testid="create-pipeline-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Pipeline
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-white">Create New Pipeline</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreatePipeline} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Pipeline Name *</Label>
                  <Input
                    value={newPipeline.name}
                    onChange={(e) => setNewPipeline({...newPipeline, name: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="e.g., UK Marketing Agencies Q1"
                    data-testid="pipeline-name-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email Focus</Label>
                  <Input
                    value={newPipeline.email_focus}
                    onChange={(e) => setNewPipeline({...newPipeline, email_focus: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="e.g., Promoting Beta trial for UK Marketing Agency Network"
                    data-testid="pipeline-email-focus"
                  />
                  <p className="text-xs text-slate-500">What's the main purpose of outreach emails in this pipeline?</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Landing Page URL</Label>
                  <Input
                    value={newPipeline.landing_page_url}
                    onChange={(e) => setNewPipeline({...newPipeline, landing_page_url: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="https://your-landing-page.com"
                    data-testid="pipeline-landing-url"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Campaign Message / AI Prompt</Label>
                  <textarea
                    value={newPipeline.campaign_message}
                    onChange={(e) => setNewPipeline({...newPipeline, campaign_message: e.target.value})}
                    className="w-full min-h-[100px] bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    placeholder="Custom instructions for AI-generated outreach emails..."
                    data-testid="pipeline-campaign-message"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setShowCreatePipelineDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-emerald-600 hover:bg-emerald-700" 
                    disabled={creatingPipeline}
                    data-testid="submit-pipeline"
                  >
                    {creatingPipeline ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Pipeline
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Import Leads Button */}
          <Dialog open={showImportDialog} onOpenChange={(open) => {
            setShowImportDialog(open);
            if (!open) {
              setImportFile(null);
              setImportPipelineId('');
              if (fileInputRef.current) fileInputRef.current.value = '';
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10" data-testid="import-leads-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2">
                  <FileSpreadsheet className="w-6 h-6 text-cyan-400" />
                  Import Leads from File
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Upload a CSV or Excel file to import leads into a pipeline
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Select Pipeline *</Label>
                  <Select value={importPipelineId} onValueChange={setImportPipelineId}>
                    <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="import-pipeline-select">
                      <SelectValue placeholder="Choose a pipeline..." />
                    </SelectTrigger>
                    <SelectContent>
                      {pipelines.filter(p => !p.shared_from).map(pipeline => (
                        <SelectItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name} ({pipeline.lead_count || 0} leads)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-slate-300">Upload File *</Label>
                  <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center hover:border-cyan-500/50 transition-colors">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="import-file-input"
                      data-testid="import-file-input"
                    />
                    <label htmlFor="import-file-input" className="cursor-pointer">
                      {importFile ? (
                        <div className="space-y-2">
                          <FileSpreadsheet className="w-10 h-10 mx-auto text-cyan-400" />
                          <p className="text-sm text-white font-medium">{importFile.name}</p>
                          <p className="text-xs text-slate-400">
                            {(importFile.size / 1024).toFixed(1)} KB
                          </p>
                          <p className="text-xs text-cyan-400 hover:underline">Click to change file</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="w-10 h-10 mx-auto text-slate-500" />
                          <p className="text-sm text-slate-400">
                            <span className="text-cyan-400 hover:underline">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-slate-500">CSV, Excel (.xlsx, .xls)</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div className="bg-slate-950 rounded-lg p-4 text-sm space-y-2">
                  <p className="text-slate-300 font-medium">Expected columns:</p>
                  <ul className="text-slate-400 text-xs space-y-1">
                    <li>• <span className="text-cyan-400">Company Name</span> (required)</li>
                    <li>• Email, Phone, Contact Name</li>
                    <li>• Website, Industry, Address</li>
                    <li>• LinkedIn URL, Notes</li>
                  </ul>
                  <p className="text-slate-500 text-xs mt-2">Column names are flexible - we'll auto-detect them.</p>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="ghost" onClick={() => setShowImportDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleImportFile}
                    className="bg-cyan-600 hover:bg-cyan-700" 
                    disabled={importing || !importFile || !importPipelineId}
                    data-testid="confirm-import-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import Leads
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Lead Button */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 glow-blue btn-press" data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-white">Add New Lead</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddLead} className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Company Name *</Label>
                  <Input
                    value={newLead.company_name}
                    onChange={(e) => setNewLead({...newLead, company_name: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="Acme Inc"
                    data-testid="lead-company-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Domain</Label>
                  <Input
                    value={newLead.company_domain}
                    onChange={(e) => setNewLead({...newLead, company_domain: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="acme.com"
                    data-testid="lead-domain"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Contact Name</Label>
                  <Input
                    value={newLead.contact_name}
                    onChange={(e) => setNewLead({...newLead, contact_name: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="John Smith"
                    data-testid="lead-contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Title</Label>
                  <Input
                    value={newLead.contact_title}
                    onChange={(e) => setNewLead({...newLead, contact_title: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="Marketing Director"
                    data-testid="lead-title"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={newLead.contact_email}
                    onChange={(e) => setNewLead({...newLead, contact_email: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="john@acme.com"
                    data-testid="lead-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">LinkedIn URL</Label>
                  <Input
                    value={newLead.contact_linkedin}
                    onChange={(e) => setNewLead({...newLead, contact_linkedin: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="linkedin.com/in/john"
                    data-testid="lead-linkedin"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Industry</Label>
                  <Select 
                    value={newLead.industry} 
                    onValueChange={(v) => setNewLead({...newLead, industry: v})}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800" data-testid="lead-industry">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Marketing">Marketing</SelectItem>
                      <SelectItem value="Design">Design</SelectItem>
                      <SelectItem value="Advertising">Advertising</SelectItem>
                      <SelectItem value="Brand">Brand</SelectItem>
                      <SelectItem value="Tech">Tech</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Staff Count</Label>
                  <Input
                    type="number"
                    value={newLead.staff_count}
                    onChange={(e) => setNewLead({...newLead, staff_count: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="25"
                    data-testid="lead-staff"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Location</Label>
                  <Input
                    value={newLead.location}
                    onChange={(e) => setNewLead({...newLead, location: e.target.value})}
                    className="bg-slate-950 border-slate-800"
                    placeholder="London"
                    data-testid="lead-location"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-lead">
                  Add Lead
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search leads..."
            className="pl-10 bg-slate-900 border-slate-800"
            data-testid="search-leads"
          />
        </div>
        <Select value={filterWarm} onValueChange={setFilterWarm}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-800" data-testid="filter-warm">
            <Filter className="w-4 h-4 mr-2 text-slate-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leads</SelectItem>
            <SelectItem value="hot">Hot Only</SelectItem>
            <SelectItem value="warm">Warm Only</SelectItem>
            <SelectItem value="lukewarm">Lukewarm</SelectItem>
            <SelectItem value="cold">Cold</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-slate-900 border-slate-800" data-testid="filter-status">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="follow_up_needed">
              <span className="flex items-center gap-1 text-amber-400">
                <RefreshCcw className="w-3 h-3" />
                Needs Follow-up
              </span>
            </SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="queued">Queued</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="replied">
              <span className="flex items-center gap-1 text-emerald-400">
                <MessageSquareReply className="w-3 h-3" />
                Replied
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterPipeline} onValueChange={setFilterPipeline}>
          <SelectTrigger className="w-48 bg-slate-900 border-slate-800" data-testid="filter-pipeline">
            <SelectValue placeholder="All Pipelines" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Pipelines</SelectItem>
            {pipelines.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} ({p.lead_count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Export Button */}
        {filterPipeline !== 'all' && (
          <Button
            variant="outline"
            size="sm"
            onClick={exportPipelineCSV}
            className="bg-slate-900 border-slate-800 hover:bg-slate-800"
            data-testid="export-pipeline-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        )}
        
        {/* Generate Emails Button */}
        {filterPipeline !== 'all' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => generatePipelineEmails(filterPipeline)}
            disabled={generatingPipelineEmails}
            className="bg-violet-900/50 border-violet-500/50 text-violet-300 hover:bg-violet-800/50 hover:text-violet-200"
            data-testid="generate-pipeline-emails-btn"
          >
            {generatingPipelineEmails ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate Emails
              </>
            )}
          </Button>
        )}
        
        {/* Generate Follow-ups Button */}
        {filterPipeline !== 'all' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => generatePipelineFollowups(filterPipeline)}
            disabled={generatingPipelineFollowups}
            className="bg-amber-900/50 border-amber-500/50 text-amber-300 hover:bg-amber-800/50 hover:text-amber-200"
            data-testid="generate-pipeline-followups-btn"
          >
            {generatingPipelineFollowups ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <RefreshCcw className="w-4 h-4 mr-2" />
                Follow-ups
              </>
            )}
          </Button>
        )}
        
        {/* Bulk Delete Pipelines */}
        {pipelines.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <Checkbox
              checked={selectedPipelineIds.size === pipelines.length && pipelines.length > 0}
              onCheckedChange={toggleSelectAllPipelines}
              className="border-slate-600"
              data-testid="select-all-pipelines"
            />
            <span className="text-xs text-slate-400">Select All</span>
            {selectedPipelineIds.size > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={bulkDeletePipelines}
                disabled={bulkDeletingPipelines}
                className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8"
                data-testid="bulk-delete-pipelines"
              >
                {bulkDeletingPipelines ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-1" />
                )}
                Delete {selectedPipelineIds.size}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Bulk Actions Bar for Leads */}
      {selectedLeadIds.size > 0 && (
        <div className="flex items-center gap-4 p-3 bg-slate-800/50 border border-slate-700 rounded-lg">
          <span className="text-sm text-slate-300">
            {selectedLeadIds.size} lead{selectedLeadIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={bulkGenerateEmailsForSelected}
              disabled={bulkActionLoading}
              className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
              data-testid="bulk-generate-emails"
            >
              {bulkActionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Sparkles className="w-4 h-4 mr-1" />
              )}
              Generate Emails
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={bulkRemoveFromPipeline}
              disabled={bulkActionLoading}
              className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              data-testid="bulk-remove-from-pipeline"
            >
              {bulkActionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <ChevronRight className="w-4 h-4 mr-1" />
              )}
              Remove from Pipeline
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={bulkDeleteLeads}
              disabled={bulkActionLoading}
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              data-testid="bulk-delete-leads"
            >
              {bulkActionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <Trash2 className="w-4 h-4 mr-1" />
              )}
              Delete Permanently
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedLeadIds(new Set())}
            className="ml-auto text-slate-400"
          >
            Clear Selection
          </Button>
        </div>
      )}

      {/* Pipeline Cards with Sharing */}
      {pipelines.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
          {pipelines.map((p) => (
            <Card 
              key={p.id} 
              className={`bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors ${
                p.is_shared ? 'border-l-4 border-l-purple-500' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white truncate">{p.name}</h3>
                    {p.is_shared && (
                      <div className="flex items-center gap-1 text-xs text-purple-400 mt-1">
                        <Users className="w-3 h-3" />
                        <span>Shared by {p.shared_by}</span>
                      </div>
                    )}
                  </div>
                  <Badge className="bg-blue-500/20 text-blue-400 ml-2">
                    {p.lead_count} leads
                  </Badge>
                </div>
                {p.campaign_message && (
                  <p className="text-xs text-slate-400 mb-3 line-clamp-2">{p.campaign_message}</p>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFilterPipeline(p.id)}
                    className="flex-1 text-slate-400 hover:text-white hover:bg-slate-800"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    View
                  </Button>
                  {!p.is_shared && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSharingPipeline(p);
                        setShowShareDialog(true);
                      }}
                      className="text-purple-400 hover:text-purple-300 hover:bg-purple-900/20"
                      data-testid={`share-pipeline-${p.id}`}
                    >
                      <Share2 className="w-3 h-3 mr-1" />
                      Share
                    </Button>
                  )}
                </div>
                {p.shared_with?.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-slate-800">
                    <p className="text-xs text-slate-500">
                      Shared with {p.shared_with.length} user{p.shared_with.length > 1 ? 's' : ''}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Share Pipeline Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              Share Pipeline: {sharingPipeline?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Share with (email)</Label>
              <Input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="colleague@company.com"
                className="bg-slate-950 border-slate-800"
                data-testid="share-email-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              The user will be able to view this pipeline and its leads. They won't be able to edit or delete it.
            </p>
            <div className="flex justify-end gap-3">
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowShareDialog(false);
                  setSharingPipeline(null);
                  setShareEmail('');
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSharePipeline}
                disabled={sharing || !shareEmail.trim()}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-share-btn"
              >
                {sharing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sharing...</>
                ) : (
                  <><Share2 className="w-4 h-4 mr-2" /> Share Pipeline</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leads Table */}
      <Card className="bg-slate-900/50 border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table stack-mobile">
            <thead>
              <tr>
                <th className="w-12">
                  <Checkbox
                    checked={filteredLeads.length > 0 && selectedLeadIds.size === filteredLeads.length}
                    onCheckedChange={toggleSelectAllLeads}
                    className="border-slate-600"
                    data-testid="select-all-leads"
                  />
                </th>
                <th>Company</th>
                <th>Contact</th>
                <th>ICP Score</th>
                <th>Warm Score</th>
                <th>Signals</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={8}>
                      <div className="h-12 bg-slate-800/50 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : filteredLeads.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-500">
                    No leads found. Add your first lead to get started.
                  </td>
                </tr>
              ) : (
                filteredLeads.map((lead) => (
                  <tr 
                    key={lead.id} 
                    className={`cursor-pointer border-l-2 ${
                      lead.warm_label === 'hot' ? 'border-l-red-500' :
                      lead.warm_label === 'warm' ? 'border-l-orange-500' :
                      lead.warm_label === 'lukewarm' ? 'border-l-yellow-500' :
                      'border-l-slate-700'
                    } ${selectedLeadIds.has(lead.id) ? 'bg-slate-800/50' : ''}`}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedLeadIds.has(lead.id)}
                        onCheckedChange={() => toggleLeadSelect(lead.id)}
                        className="border-slate-600"
                        data-testid={`select-lead-${lead.id}`}
                      />
                    </td>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-slate-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{lead.company_name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            {lead.company_domain && (
                              <>
                                <Globe className="w-3 h-3" />
                                <span>{lead.company_domain}</span>
                              </>
                            )}
                            {lead.staff_count && (
                              <span>• {lead.staff_count} staff</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Contact">
                      <div>
                        <p className="text-slate-200">{lead.contact_name || '-'}</p>
                        <p className="text-xs text-slate-500">{lead.contact_title || lead.contact_email}</p>
                      </div>
                    </td>
                    <td data-label="ICP score">
                      <div className="w-32">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-mono text-slate-400">ICP</span>
                          <span className="text-xs font-mono text-emerald-400">{lead.icp_score}</span>
                        </div>
                        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full transition-all"
                            style={{ width: `${lead.icp_score}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td data-label="Warm score">
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-mono text-xs ${getWarmStyle(lead.warm_label)}`}>
                        {getWarmIcon(lead.warm_label)}
                        <span>{lead.warm_score}</span>
                      </div>
                    </td>
                    <td data-label="Signals">
                      <div className="flex flex-wrap gap-1.5 max-w-[200px]">
                        {lead.linkedin_follows_you && (
                          <span className="signal-chip signal-linkedin">
                            <Linkedin className="w-3 h-3" />
                            Follows
                          </span>
                        )}
                        {lead.site_visitor_count > 0 && (
                          <span className="signal-chip signal-visitor">
                            <Eye className="w-3 h-3" />
                            Visited
                          </span>
                        )}
                        {lead.competitor_left_signal && (
                          <span className="signal-chip signal-competitor">
                            <Swords className="w-3 h-3" />
                            Left {lead.competitor_left_name}
                          </span>
                        )}
                        {lead.signals?.slice(0, 2).map((signal, i) => (
                          <span key={i} className={`signal-chip ${getSignalStyle(signal.type)}`}>
                            {getSignalIcon(signal.type)}
                            {signal.type}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td data-label="Status">
                      {getStatusBadge(lead.status)}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {/* Generate Outreach Button - hide for replied/sent/queued */}
                        {!['queued', 'sent', 'replied'].includes(lead.status) && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={`text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 ${
                              !lead.contact_email && !lead.contact_linkedin ? 'opacity-50' : ''
                            }`}
                            disabled={generatingOutreach === lead.id || (!lead.contact_email && !lead.contact_linkedin)}
                            onClick={(e) => {
                              e.stopPropagation();
                              generateOutreach(lead);
                            }}
                            title={
                              !lead.contact_email && !lead.contact_linkedin 
                                ? 'Add email or LinkedIn first' 
                                : 'Generate outreach and add to approval queue'
                            }
                            data-testid={`generate-outreach-${lead.id}`}
                          >
                            {generatingOutreach === lead.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        {lead.status === 'replied' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            title="View reply"
                            data-testid={`view-reply-${lead.id}`}
                          >
                            <MessageSquareReply className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-slate-400 hover:text-blue-400 hover:bg-blue-500/10"
                          title="Edit contact details"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(lead);
                          }}
                          data-testid={`edit-lead-${lead.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                          title="Remove from pipeline"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteLead(lead.id, lead.company_name);
                          }}
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <CreateDealButton
                          source="pipeline"
                          testId={`create-deal-lead-${lead.id}`}
                          prefill={{
                            company_name: lead.company_name,
                            contact_name: lead.contact_name,
                            contact_email: lead.contact_email,
                            contact_phone: lead.contact_phone,
                            lead_id: lead.id,
                          }}
                        />
                        <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="leads-pagination">
          <p className="text-sm text-slate-500">
            Showing {(currentPage - 1) * PAGE_SIZE + 1} - {Math.min(currentPage * PAGE_SIZE, totalLeadCount)} of {totalLeadCount}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => { setCurrentPage(p => p - 1); fetchLeads(currentPage - 1); }}
              className="border-slate-700 text-slate-300"
              data-testid="leads-prev-page"
            >
              Previous
            </Button>
            <span className="text-sm text-slate-400 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => { setCurrentPage(p => p + 1); fetchLeads(currentPage + 1); }}
              className="border-slate-700 text-slate-300"
              data-testid="leads-next-page"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Edit Lead Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Contact Details</DialogTitle>
          </DialogHeader>
          {editingLead && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-400">Contact Name</Label>
                <Input
                  value={editingLead.contact_name || ''}
                  onChange={(e) => setEditingLead({...editingLead, contact_name: e.target.value})}
                  placeholder="John Smith"
                  className="bg-slate-950 border-slate-800"
                  data-testid="edit-contact-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400">Email Address</Label>
                <Input
                  type="email"
                  value={editingLead.contact_email || ''}
                  onChange={(e) => setEditingLead({...editingLead, contact_email: e.target.value})}
                  placeholder="john@company.com"
                  className="bg-slate-950 border-slate-800"
                  data-testid="edit-contact-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400">Job Title</Label>
                <Input
                  value={editingLead.contact_title || ''}
                  onChange={(e) => setEditingLead({...editingLead, contact_title: e.target.value})}
                  placeholder="Marketing Director"
                  className="bg-slate-950 border-slate-800"
                  data-testid="edit-contact-title"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-blue-400" />
                  LinkedIn Profile URL
                </Label>
                <Input
                  value={editingLead.contact_linkedin || ''}
                  onChange={(e) => setEditingLead({...editingLead, contact_linkedin: e.target.value})}
                  placeholder="https://linkedin.com/in/johnsmith"
                  className="bg-slate-950 border-slate-800"
                  data-testid="edit-contact-linkedin"
                />
                <p className="text-xs text-slate-600">Paste the full LinkedIn profile URL</p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditDialog(false);
                    setEditingLead(null);
                  }}
                  className="border-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveLeadEdit}
                  disabled={savingEdit}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-lead-edit"
                >
                  {savingEdit ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pipeline;
